// A page that shows the design system to itself, at /design.
//
// Not part of the site a player sees. It exists so the tokens can be checked
// against `Cinemore Quizzes.dc.html` side by side — particularly the values
// CLAUDE.md marks as chosen for the build rather than read off the design, which
// are the ones most likely to be wrong.
import { useState } from 'react';
import { Modal } from '../components/Modal.tsx';

// Only the token names and what each is for. **The value is read from the token
// itself** (`valueOf`, below), never written here: a page whose job is to show
// what the colours are is the last place a second copy of them should live, and
// the copy is exactly what went stale the first time the palette changed.
const COLOURS: [string, string][] = [
  ['--bg', 'Page'],
  ['--surface', 'Header, cards, panels, modals'],
  ['--border', 'Borders'],
  ['--rule', 'Rules between rows'],
  ['--heading', 'Section headings'],
  ['--accent', 'Filled primary actions, bars'],
  ['--accent-bright', 'Links, focus and hover borders, highlighted bar'],
  ['--text', 'Body text'],
  ['--muted', 'neutral-500: secondary links, footer, years'],
  ['--grey', 'Missed'],
  ['--tab-selected', 'Selected tab, dropdown highlight']
];

const CHOSEN: string[] = [
  '--muted-light', '--accent-pale', '--accent-hover', '--accent-pressed', '--link-hover',
  '--button-label', '--tab-text', '--dropdown-bg',
  '--missed-tile-text', '--deep-accent', '--overlay'
];

const valueOf = (token: string) =>
  typeof document === 'undefined' ? ''
    : getComputedStyle(document.documentElement).getPropertyValue(token).trim();

const TYPE: [string, string][] = [
  ['--size-hero', 'Index hero title'], ['--size-result', 'Result title'],
  ['--size-figure', 'Stats figures'], ['--size-modal-title', 'Modal titles'],
  ['--size-result-line', 'Result line'], ['--size-card-title', 'Index card titles'],
  ['--size-input', 'Text input'], ['--size-button', 'Buttons, section headings, guess rows'],
  ['--size-body', 'Header, index body, poster titles'], ['--size-caption', 'Instruction line, counter, links'],
  ['--size-meta', 'Meta text, cast roles'], ['--size-chart', 'Chart labels, footer'],
  ['--size-small', 'Mobile actor names, years'], ['--size-tab', 'CORRECT tab']
];

export function DesignSystem() {
  const [modal, setModal] = useState(false);
  const section = { marginTop: 'var(--section-gap)' };

  return (
    <main className="column" style={{ paddingTop: 36, paddingBottom: 28 }}>
      <h1 style={{ fontSize: 'var(--size-instructions)', letterSpacing: '-0.025em' }}>Design system</h1>
      <p className="meta" style={{ marginTop: 8 }}>
        Every token the site uses. Values marked chosen are placeholders to check against the design.
      </p>

      <section style={section}>
        <h2 className="heading">Colours</h2>
        <hr className="rule" style={{ margin: '8px 0 12px' }} />
        {COLOURS.map(([token, use]) => (
          <div key={token} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
            <span style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)',
                           border: '1px solid var(--border)', background: `var(${token})` }} />
            <code style={{ fontSize: 'var(--size-meta)', minWidth: 140 }}>{token}</code>
            <span className="meta" style={{ minWidth: 76 }}>{valueOf(token)}</span>
            <span className="meta">{use}</span>
          </div>
        ))}
      </section>

      <section style={section}>
        <h2 className="heading">Chosen for the build, not from the design</h2>
        <hr className="rule" style={{ margin: '8px 0 12px' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {CHOSEN.map((token) => (
            <div key={token} className="panel" style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid var(--border)',
                             background: `var(${token})` }} />
              <code style={{ fontSize: 'var(--size-small)' }}>{token}</code>
              <span className="meta" style={{ fontSize: 'var(--size-small)' }}>{valueOf(token)}</span>
            </div>
          ))}
        </div>
      </section>

      <section style={section}>
        <h2 className="heading">Type</h2>
        <hr className="rule" style={{ margin: '8px 0 12px' }} />
        {TYPE.map(([token, use]) => (
          <div key={token} style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '4px 0' }}>
            <span style={{ fontSize: `var(${token})`, minWidth: 160 }}>Cinemore 2026</span>
            <code className="meta">{token}</code>
            <span className="meta">{use}</span>
          </div>
        ))}
      </section>

      <section style={section}>
        <h2 className="heading">Buttons and input</h2>
        <hr className="rule" style={{ margin: '8px 0 12px' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary">Primary</button>
          <button className="btn">Secondary</button>
          <button className="btn btn-primary" disabled>Disabled</button>
          <button className="link-button" type="button">How it works</button>
          <button className="btn" type="button" onClick={() => setModal(true)}>Open a modal</button>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <input className="input" placeholder="Name the movie or show" />
          <button className="btn btn-primary" style={{ width: 104, flex: 'none' }}>Submit</button>
        </div>
        <span className="caption" style={{ marginTop: 14 }}>Guesses remaining: 2</span>
      </section>

      <section style={section}>
        <h2 className="heading">Surfaces</h2>
        <hr className="rule" style={{ margin: '8px 0 12px' }} />
        <div className="panel">
          <h3 className="heading">Panel</h3>
          <p className="meta" style={{ margin: '6px 0 0' }}>Cards and panels share a surface, a border and a 10px radius.</p>
        </div>
      </section>

      <Modal title="Modal" open={modal} onClose={() => setModal(false)}>
        <p className="meta" style={{ margin: 0 }}>
          Centred in the viewport, 440px, close button top right. The site header stays
          visible behind the overlay.
        </p>
      </Modal>
    </main>
  );
}
